import ApiClient from '@/services/apiClient';
import ReaderDefaultConfig from './readerDefaultConfig';

export default class Facility {
    name;
    /** Name of the client owning the facility (mandatory at creation, renames the client on update) */
    client_name;
    address;
    country;
    admin_email;
    default_config;
    installation_type;  //"cloud", "on_premise", "client_hardware"
    picture;
    timezone;
    creation_date;
    id;
    database;
    endpoint;
    domain_prefix;
    validated;
    /** The facility awaits its administrator validation (read-only, set by the backend) */
    pending_validation = false;
    is_leasing;
    hexa_identifier;
    components_auto_update;
    components_auto_update_time;
    blocked_installers;
    blocked_managers;
    alert_counter = 0;
    hubs;
    loaded = false;
    components = {
      hubs: {
        type: "facilityComponents.hubs",
        class: 'mdi mdi-server-network text-primary',
        count: 0,
      },
      readers: {
        type: "facilityComponents.readers",
        class: 'mdi mdi-key-wireless text-success',
        count: 0,
      },
      extensions: {
        type: "facilityComponents.extensions",
        class: 'mdi mdi-puzzle-outline text-success',
        count: 0,
      }
    };
    external_Alarms;
    card_profiles = [];

    constructor() {
      this.id = 0;
      this.name = '';
      this.client_name = '';
      this.address = '';
      this.country = 'France';
      this.admin_email = '';
      this.default_config = new ReaderDefaultConfig();
      this.installation_type = 'cloud';
      this.timezone = "Europe/Paris";
      this.creation_date = new Date();
      this.database = "";
      this.endpoint = "";
      this.domain_prefix = "";
      this.validated = "";
      this.pending_validation = false;
      this.is_leasing = false;
      this.hexa_identifier = false;
      this.blocked_installers = "";
      this.blocked_managers = "";
      this.components_auto_update = true;
      this.components_auto_update_time = 3600;
      this.hubs = [];
    }
        
    static constructFromObject(data, obj) {
      if (data) {
        obj = obj || new Facility();
        obj.name = ApiClient.convertToType(data['name'], 'String');
        obj.client_name = ApiClient.convertToType(data['client_name'], 'String')?? '';
        obj.address = ApiClient.convertToType(data['address'], 'String');
        obj.country = ApiClient.convertToType(data['country'], 'String');
        obj.admin_email = ApiClient.convertToType(data['admin_email'], 'String');
        obj.installation_type = ApiClient.convertToType(data['installation_type'], 'String');
        obj.default_config = ReaderDefaultConfig.constructFromObject(data['default_config']);
        obj.picture = ApiClient.convertToType(data['picture'], 'String');
        obj.timezone = ApiClient.convertToType(data['timezone'], 'String');
        obj.creation_date = ApiClient.convertToType(data['creation_date'], 'Date');
        obj.id = ApiClient.convertToType(data['id'], 'Integer');
        obj.database = ApiClient.convertToType(data['database'], 'String');
        obj.endpoint = ApiClient.convertToType(data['endpoint'], 'String');
        obj.domain_prefix = ApiClient.convertToType(data['domain_prefix'], 'String');
        obj.validated = ApiClient.convertToType(data['validated'], 'Boolean');
        obj.pending_validation = ApiClient.convertToType(data['pending_validation'], 'Boolean')?? false;
        obj.alert_counter = ApiClient.convertToType(data['alert_counter'], 'Integer');
        obj.blocked_installers = ApiClient.convertToType(data['blocked_installers'], 'Boolean');
        obj.blocked_managers = ApiClient.convertToType(data['blocked_managers'], 'Boolean');
        obj.components_auto_update = 'components_auto_update_time' in data;
        obj.components_auto_update_time = ApiClient.convertToType(data['components_auto_update_time'], 'Integer')?? 3600;
        obj.is_leasing = ApiClient.convertToType(data['is_leasing'], 'Boolean')?? false;
        obj.hexa_identifier = ApiClient.convertToType(data['hexa_identifier'], 'Boolean')?? false;
      }
      return obj;
    }

    toJSON() {
      const json = {
        name: this.name,
        address: this.address,
        country: this.country,
        default_config: this.default_config,
        installation_type: this.installation_type,
        timezone: this.timezone,
        is_leasing: this.is_leasing,
        hexa_identifier: this.hexa_identifier,
      };

      if(this.admin_email) {
        json.admin_email = this.admin_email;
      }

      if(this.client_name) {
        json.client_name = this.client_name;
      }

      if(this.id === 0) {
        json.creation_date = new Date();
      }

      if(this.components_auto_update) {
        json.components_auto_update_time = this.components_auto_update_time;
      }
  
      if (this.picture != null && !this.picture.startsWith("http")) {
        if (this.picture.length === 0) {
          json.picture = "";
        } else {
          json.picture = this.picture.split(',')[1];
        }
      }
      return json;
    }

    clone() {
      let facility = new Facility();
      facility.id = this.id;
      facility.name = this.name;
      facility.client_name = this.client_name;
      facility.admin_email = this.admin_email;
      facility.address = this.address;
      facility.country = this.country;
      facility.picture = this.picture;
      facility.default_config = this.default_config;
      facility.installation_type = this.installation_type;
      facility.timezone = this.timezone;
      facility.validated = this.validated;
      facility.pending_validation = this.pending_validation;
      facility.is_leasing = this.is_leasing;
      facility.hexa_identifier = this.hexa_identifier;
      facility.components_auto_update = this.components_auto_update;
      facility.components_auto_update_time = this.components_auto_update_time;
      return facility;
    }

    update(updatedFacility, picture) {
      this.name = updatedFacility.name;
      this.client_name = updatedFacility.client_name;
      this.admin_email = updatedFacility.admin_email;
      this.address = updatedFacility.address;
      this.country = updatedFacility.country;
      this.is_leasing = updatedFacility.is_leasing;
      this.hexa_identifier = updatedFacility.hexa_identifier;
      this.components_auto_update = updatedFacility.components_auto_update;
      this.components_auto_update_time = updatedFacility.components_auto_update_time;
      this.picture = picture;
    }

    updateComponents() {
      let totalReaders = 0;
      let totalExtensions = 0;
      this.hubs.forEach(hub => {
        totalReaders += hub.totalReaders;
        totalExtensions += hub.totalExtensions;
      });
      this.components.hubs.count = this.hubs.length;
      this.components.readers.count = totalReaders;
      this.components.extensions.count = totalExtensions;
    }

    get initials() {
      const nameParts = this.name.split(' ');
      if (nameParts.length === 1) {
        return this.name.substring(0, 2).toUpperCase();
      } else {
        const initials = nameParts.map(part => part.charAt(0).toUpperCase());
        return initials.join('');
      }  
    }

    get managerURL() {
      if(this.installation_type === 'cloud') {
        return `https://${this.domain_prefix}.${process.env.VUE_APP_MANAGER_URL}`;
      } else {
        return 'https://haxokey/manager';
      }
    }
}
